import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

export default function Sessions() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [sessions, setSessions] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    api
      .get(`/sessions${showArchived ? "?archived=true" : ""}`)
      .then((r) => setSessions(r.data))
      .catch(() => setSessions([]));
  }, [showArchived]);

  if (sessions === null) return <div className="tb-loading">{t("common.loading")}</div>;

  const now = Date.now();
  const shown = showArchived
    ? sessions
    : sessions.filter((s) => {
        const ts = new Date(s.scheduled_at).getTime();
        if (filter === "upcoming") return ts > now;
        if (filter === "past") return ts <= now;
        return true;
      });

  return (
    <div className="tb-page">
      <h1 className="tb-h1" data-testid="sessions-heading">
        {t("sessions.title")}
      </h1>
      <div style={{ display: "flex", gap: 6, marginBottom: "1.5rem", flexWrap: "wrap", alignItems: "center" }}>
        {!showArchived && [
          ["all", t("sessions.filterAll")],
          ["upcoming", t("sessions.filterUpcoming")],
          ["past", t("sessions.filterPast")],
        ].map(([k, lbl]) => (
          <button
            key={k}
            className={filter === k ? "tb-primary-btn" : "tb-ghost-btn"}
            style={{ padding: "8px 16px", fontSize: "0.82rem" }}
            onClick={() => setFilter(k)}
            data-testid={`filter-${k}`}
          >
            {lbl}
          </button>
        ))}
        <button
          className={showArchived ? "tb-primary-btn" : "tb-ghost-btn"}
          style={{ padding: "8px 16px", fontSize: "0.82rem", marginLeft: showArchived ? 0 : "auto" }}
          onClick={() => setShowArchived(!showArchived)}
          data-testid="filter-archived"
        >
          {showArchived ? `← ${t("sessions.backToActive")}` : t("sessions.viewArchive")}
        </button>
      </div>

      {shown.length === 0 ? (
        <p className="tb-muted" data-testid="sessions-empty">
          {showArchived ? t("sessions.archiveEmpty") : t("dash.noUpcoming")}
        </p>
      ) : (
        <div className="tb-card-grid" data-testid="sessions-grid">
          {shown.map((s) => {
            const dt = new Date(s.scheduled_at);
            const is11 = s.type === "internal_1_1";
            const href = is11 ? `/app/team-1-1/${s.id}` : `/app/sessions/${s.id}`;
            const other = is11
              ? ((s.participants || []).find((p) => p.id !== user?.id)?.name || (s.participants || [])[0]?.name || "")
              : `${s.advisor?.name || "?"} · ${s.entrepreneur?.name || "?"}`;
            return (
              <Link key={s.id} to={href} className="tb-app-card" data-testid={`session-item-${s.id}`}>
                <span className="tb-when">
                  {dt.toLocaleString(lang === "fr" ? "fr-CA" : "en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <strong>{s.title || (is11 ? "1:1" : "Advisory session")}</strong>
                <small>{other}</small>
                <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                  <span className={`tb-badge ${is11 ? "accent" : ""}`}>
                    {is11 ? `👥 ${t("team1v1.badge.internal_1_1")}` : `🎓 ${t("team1v1.badge.advisory")}`}
                  </span>
                  {(s.has_summary || s.has_team_summary) && <span className="tb-badge good">✓ Summary</span>}
                  {!is11 && s.has_pre_form && !s.has_summary && <span className="tb-badge accent">{t("sessions.preDone")}</span>}
                  {!is11 && !s.has_pre_form && <span className="tb-badge warn">{t("sessions.preMissing")}</span>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
